import { useCallback, useState } from "react";

import { omit } from "lodash";
import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  useMetricsExplorerContext,
  useMetricsExplorerStateContext,
} from "@features/metrics-explorer/hooks";
import {
  CREATE_METRICS_EXPLORER_SAVED_VIEW,
  GET_METRICS_EXPLORER_SAVED_VIEWS,
} from "@gql/metrics-explorer-saved-views";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type CreateMetricsExplorerSavedView,
  type CreateMetricsExplorerSavedViewVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

interface Props {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const CreateSavedViewsModal = ({
  isOpen,
  onConfirm,
  onCancel,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { filters, tiles, chartState } = useMetricsExplorerContext();
  const { selectSavedView } = useMetricsExplorerStateContext();

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [createSavedView] = useNorthbeamMutation<
    CreateMetricsExplorerSavedView,
    CreateMetricsExplorerSavedViewVariables
  >(CREATE_METRICS_EXPLORER_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_METRICS_EXPLORER_SAVED_VIEWS }],
  });
  const toast = useToasts();

  const saveSavedView = async () => {
    prettyLogEventPage({
      domain: "Metrics Explorer",
      action: "onChange",
      additionalActionInfo: "Create Saved View",
    });

    try {
      if (name) {
        setIsSaving(true);
        const { data } = await createSavedView({
          variables: {
            name,
            config: {
              filters: omit(filters, ["dateRangeInterval"]),
              tiles,
              chartState,
            },
          },
        });

        if (data) {
          selectSavedView(data.createMetricsExplorerSavedView);
        }

        setName("");
        onConfirm();
        toast.addToast("Saved view created", { appearance: "success" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = useCallback(() => {
    onCancel();
    setName("");
  }, [onCancel]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancel}
      headerTitleProps={{ className: "text-xl font-semibold" }}
      titleContent={<div>Save as new?</div>}
    >
      <TextInput
        label="SAVED VIEW NAME"
        onChange={({ target }) => setName(target.value)}
        value={name}
      />
      <div className="flex space-x-3 mt-12">
        <PrimaryButton
          isDisabled={!name || isSaving}
          loading={isSaving}
          onClick={saveSavedView}
        >
          YES, SAVE
        </PrimaryButton>
        <SecondaryButton disabled={isSaving} onClick={cancel}>
          NO, CANCEL
        </SecondaryButton>
      </div>
    </Modal>
  );
};
